import {ref, unref, computed, watch} from 'vue';
import {useEventListener, reactiveComputed} from '@vueuse/core';
import {defineStore, storeToRefs} from 'pinia';

import {MessageBox, Message} from '@baidu/acud-element-ui';
import {CHAT_MODE, SCENE, DISPLAY_TYPE, DIALOG_TYPE, constant} from '@/znzs/new_src/constant';
import pinia from '@/znzs/new_src/store/index.js';
import {createDialogId, safeOpenTab, upload} from '@/znzs/new_src/utils';
import IMSocket from '@/znzs/new_src/utils/ImSocket';
import {useCommonStoreOut} from './commonStore';
import {isInSession, getIMSessions, sendMessage, cleanUnreadMsg, cleanSingleUnreadMsg} from '@/znzs/src/api/chat.js';
import {CHANNEL} from '@/znzs/src/utils/enum';
import {isSandbox} from '@/znzs/src/utils/env';
import {checkRealname, checkLLMNumber} from '@/znzs/new_src/api';
import store from '@/znzs/new_src/vuex';

const IM_CONFIG_MAP = {
    [CHAT_MODE.PRESALE]: {
        connectConfig: {
            productId: 69,
            questionId: isSandbox ? 610 : 425,
        },
        errorConfig: {
            formUrl: constant.true.contactUsLink,
        },
    },
    [CHAT_MODE.QF]: {
        connectConfig: {
            productId: 278,
            questionId: 1512,
        },
        defaultSource: 'https://cloud.baidu.com/product/llmservice.html',
        errorConfig: {
            message: '当前在线咨询无人值班，您可预约大模型专属顾问回访，点击“立即预约”，提交成功后我们会尽快与您电话沟通，请保持电话畅通。',
            confirmButtonText: '立即预约',
            formUrl: 'https://cloud.baidu.com/survey/vas.html',
        },
    },
    [CHAT_MODE.MARKET]: {
        connectConfig: {
            productId: 69,
            questionId: 424,
        },
        defaultSource: 'https://qianfanmarket.baidu.com',
        errorConfig: {
            formUrl: 'https://cloud.baidu.com/survey/requirements.html?from=zuoxi',
        },
    },
    [CHAT_MODE.KEYUE]: {
        connectConfig: {
            productId: 101,
            questionId: 1320,
        },
        defaultSource: 'https://keyue.baidu.com',
        errorConfig: {
            formUrl: constant.true.contactUsLink,
        },
    },
    [CHAT_MODE.YIJIAN]: {
        connectConfig: {
            productId: 291,
            questionId: 1643,
        },
        defaultSource: 'https://yijian.cloud.baidu.com/purchase',
        errorConfig: {
            formUrl: 'https://cloud.baidu.com/survey/yijian.html',
        },
    },
};

export const useImStore = defineStore('ImStore', () => {
    const chatList = ref([]);
    const sessionId = ref(null);
    const imSocket = ref(null);
    const socketConnected = ref(false);
    const commonStore = useCommonStoreOut();
    const {chatMode, isQFChat, pageSource} = storeToRefs(commonStore);

    const chatModeType = computed(() => unref(chatMode).type);
    const scene = computed(() => SCENE[unref(chatModeType)]);
    const imConfig = reactiveComputed(() => IM_CONFIG_MAP[unref(chatModeType)] ?? {});
    const source = computed(() => (
        (unref(pageSource) && unref(pageSource) !== 'undefined')
            ? unref(pageSource)
            : imConfig.defaultSource)
    );
    const authenticationRef = ref();
    let taskAfterConnected = [];

    // 添加isIm固定属性
    function publishMessage(message) {
        message.isIm = true;
        unref(chatList).push(message);
    }

    /**
     *
     * @param {*} dialogType
     * @param {*} display
     * @param {*} content
     * @param {*} dialogInfo
     * @param {*} fakeRobot
     * @returns
     */
    function pushMessageToIm(dialogType, display, content, dialogInfo = {}, fakeRobot = false) {
        const dialogId = createDialogId();
        const {dialogTime = Date.now(), ...info} = dialogInfo;
        const fakeMessage = { // 发送问候语
            dialogId,
            dialogTime,
            dialogType,
            fakeRobot, // 主要用于控制im消息上是否显示名字和时间
            dialogInfo: {
                ...info,
                content,
                display,
            },
        };
        publishMessage(fakeMessage);
        return dialogId;
    }

    /**
     * 根据dialogId移除消息
     * @param {*} dialogId
     * @returns
     */
    function removeMessage(dialogId) {
        if (!dialogId) {
            return;
        }
        const removeIndex = unref(chatList).findIndex(item => item.dialogId === dialogId);
        if (removeIndex > -1) {
            chatList.value.splice(removeIndex, 1);
        }
    }
    /**
     * 推送提示信息
     * @param {*} time
     */
    function pushTip(content, time, type = 0) {
        const tipStyle = {};
        if (type === 1) {
            tipStyle.color = '#000';
        }
        return pushMessageToIm(
            DIALOG_TYPE.TIP,
            DISPLAY_TYPE.TIP,
            content,
            {
                dialogTime: time,
                tipStyle,
            }
        );
    }

    /**
     * 清除存量的未读消息
     * @param {*} id
     * @returns
     */
    async function clearChat(id) {
        if (!id) {
            return;
        }
        try {
            await cleanUnreadMsg(id);
        }
        catch {}
    }

    /**
     * 检查是否在IM流程中
     */
    async function checkImStatus() {
        try {
            const {success, data} =  await isInSession();
            if (data?.isInImSession) {
                clearChat(data?.sessionId);
                chatList.value = data?.dialogs.map(dialog => {
                    dialog.isIm = true;
                    return dialog;
                });
                if (unref(isQFChat)) {
                    // 由于im的消息在前端没有缓存，因此在每次连接成功的时候，也要推一次
                    pushTip(
                        '智能云大模型服务专属顾问，为您量身定制产品推荐方案',
                        data?.dialogs?.length ? data?.dialogs[0].dialogTime - 1 : Date.now()
                    );
                    pushTip(
                        '您已接入人工，专属咨询顾问很荣幸为您服务',
                        data?.dialogs?.length ? data?.dialogs[0].dialogTime + 1 : Date.now(),
                        1
                    );
                }
                sessionId.value = data?.sessionId;
            }
            return success && data?.isInImSession;

        }
        catch {
            return false;
        }
    }

    /**
     * 链接到千帆IM
     */
    async function connectToQf(params = {}) {
        await getSessionInfo({
            ...imConfig.connectConfig,
            ...(params || {}),
        });
    }
    /**
     * 初始化千帆IM
     * 场景一：从千帆立即咨询过来，此时chatmode已切换成CHATMODE.QF，
     * 场景二：在助手中，通过点击按钮来进入千帆，此时的chatmode还未切换
     * 以上场景的不同主要体现在params上，场景一是默认场景，params参数应该为空，对应参数自动做响应式变化。
     * 而在场景二下，参数需要透传过去
     */
    async function initialQFIM(params) {
        try {
            // code的作用参考new_src/components/Record/hooks/index.js的实现
            let code = 0b00;
            const res = await checkRealname();
            // 已实名
            if (res.success && res.data?.realname) {
                code = 0b01;
                const {success, data: {activate}} = await checkLLMNumber();
                // 已签署协议
                if (success && activate) {
                    // 链接千帆IM
                    connectToQf(params);
                    return true;
                }
            }
            // 移除前一个认证卡片，防止定时器洪泛请求
            // new_src/components/Record/hooks/index.js
            if (unref(authenticationRef)) {
                removeMessage(unref(authenticationRef));
                authenticationRef.value = null;
            }
            authenticationRef.value = pushMessageToIm(
                DIALOG_TYPE.SYSTEM,
                DISPLAY_TYPE.QF_AUTHENTICATION,
                '',
                {
                    connectParams: params,
                    statusCode: code,
                },
                true
            );
        }
        catch (e) {
        }
    }

    /**
     * 创建IM实例
     */
    async function createSocket() {
        if (unref(isQFChat)) {
            // await initialQFIM();
            // 这里不校验实名认证与开通大模型了，直接连接IM服务
            await connectToQf();
        }
        else {
            await getSessionInfo(imConfig.connectConfig);
        }
    }

    /**
     * 坐席分配
     */
    async function getSessionInfo(payload) {
        const requestData = {
            userName: commonStore.user.name,
            scene: unref(scene),
            source: unref(source),
            ...payload,
        };
        try {
            const {success, data} = await getIMSessions(requestData);
            if (success) {
                const {createSuccess, sessionId: id, welcome = ''} = data;
                // 创建会话成功
                if (createSuccess) {
                    sessionId.value = id;
                    if (requestData.scene === SCENE[CHAT_MODE.QF]) {
                        pushMessageToIm(DIALOG_TYPE.TIP, DISPLAY_TYPE.TIP, '智能云大模型服务专属顾问，为您量身定制产品推荐方案');
                    }
                    if (welcome) {
                        pushMessageToIm(DIALOG_TYPE.TS, DISPLAY_TYPE.HTML, welcome);
                    }
                    return;
                }
            }
            throw new Error();
        }
        catch {
            const options = {
                message: '非常抱歉，当前暂时无法为您提供在线咨询服务，您可提交留言，我们收到后会尽快进行回复，谢谢。',
                title: '提示',
                confirmButtonText: '提交留言',
                formUrl: '',
                ...(imConfig.errorConfig || {}),
            };
            // 关闭页面之后重置chatmode
            window.addEventListener('beforeunload', () => {
                document.querySelector('.chat-body').style.display = 'none';
                commonStore.chatMode = {
                    type: CHAT_MODE.NORMAL,
                };
            }, false);

            MessageBox.alert(options.message, options.title, {
                confirmButtonText: options.confirmButtonText,
                close() {
                    commonStore.chatMode = {
                        type: CHAT_MODE.NORMAL,
                    };
                },
                callback(action) {
                    if (action === 'confirm' && options.formUrl) {
                        safeOpenTab(options.formUrl);
                    }
                    commonStore.chatMode = {
                        type: CHAT_MODE.NORMAL,
                    };
                },
            });
        }
        finally {
            // 让连接有效期提前结束，防止未收到关闭消息导致多次进入IM
            chatMode.value.timestamp -= 3600 * 1000;
            commonStore.setPageSource('');
        }
    }

    /**
     * 发送消息基础方法
     * @param {*} param0
     */
    async function sendToIM({content, file}) {
        try {
            const res = await sendMessage({
                channel: CHANNEL,
                sessionId: unref(sessionId),
                content,
                file,
                userName: commonStore.user.name,
            });
            if (!res.success) {
                Message.error(res.message && res.message.global);
            }
        }
        catch {

        }
    }

    async function sendContentToIM(content) {
        if (unref(socketConnected)) {
            pushMessageToIm(DIALOG_TYPE.USER_QUESTION, DISPLAY_TYPE.HTML, content);
            await sendToIM({content});
        }
    }
    async function sendFileToIM(file) {
        if (unref(socketConnected)) {
            pushMessageToIm(DIALOG_TYPE.USER_QUESTION, DISPLAY_TYPE.FILE, '', {file});
            await sendToIM({file});
        }
    }

    /**
     * 消息已读
     * @param {*} dialogId
     */
    async function readMessage(dialogId) {
        try {
            await cleanSingleUnreadMsg(dialogId, {status: 1});
        }
        catch (e) {
        }
    }

    function onMessage({data, uniqueId}) {
        unref(imSocket).ack(8, uniqueId);
        // 消息去重
        if (
            unref(chatList).length
            && unref(chatList).some(item => item.dialogId === data.dialogId)
        ) {
            return;
        }
        // 修正消息的时间
        data.dialogTime = Date.now();
        // 消息入队
        publishMessage(data);
        // 消息已读
        readMessage(data.dialogId);
    }

    function closeSocket() {
        unref(imSocket).close();
        store.commit(
            'setChatRecordsList',
            unref(chatList).filter(
                item => item.dialogInfo?.display !== DISPLAY_TYPE.QF_AUTHENTICATION
            )
        );
        imSocket.value = null;
        chatList.value = [];
        commonStore.chatMode = {
            type: CHAT_MODE.NORMAL,
        };
    }

    let cleanPasteEvent = null;

    watch(socketConnected, () => {
        if (unref(socketConnected)) {
            cleanPasteEvent = useEventListener(window, 'paste', async function onPaste(e) {
                if (!unref(socketConnected)) {
                    return;
                }
                // 后端接口限制，这里只取第一张图片，前端具有完备的粘贴多张的能力
                const files = [...(e?.clipboardData?.files || [])].slice(0, 1);
                files.forEach(file => {
                    if (file?.size > 5 * 2 ** 20) {
                        Message.warning('图片大小不得超过5MB');
                        return;
                    }
                    pushMessageToIm(DIALOG_TYPE.USER_QUESTION, DISPLAY_TYPE.IMAGE, file);
                });
            });
        }
        else if (cleanPasteEvent) {
            cleanPasteEvent();
        }
    });

    watch(sessionId, () => {
        if (unref(imSocket)) {
            unref(imSocket).close();
        }
        imSocket.value = new IMSocket(unref(sessionId), {
            onConnect() {
                socketConnected.value = true;
                if (taskAfterConnected.length) {
                    taskAfterConnected.forEach(task => task());
                    taskAfterConnected = [];
                }
            },
            onDisConnect() {
                socketConnected.value = false;
            },
            ['channel:7'](message) {
                onMessage(message);
            },
            // 客服主动关闭请求，显示评价模块
            ['channel:9']({uniqueId}) {
                unref(imSocket).ack(8, uniqueId);
                // 添加评价消息
                pushMessageToIm(DIALOG_TYPE.CUSTOMER_EVALUATE, DISPLAY_TYPE.CUSTOMER_EVALUATE, unref(sessionId), {
                    evaluation: {
                        starRating: 0,
                    },
                });
                closeSocket();
            },
            // 用户超时未响应关闭
            ['channel:10']() {
                closeSocket();
            },
        });
    });

    return {
        chatList,
        sendToIM,
        sessionId,
        initialQFIM,
        connectToQf,
        createSocket,
        sendFileToIM,
        checkImStatus,
        removeMessage,
        getSessionInfo,
        sendContentToIM,
        pushMessageToIm,
        socketConnected,
    };
});

export const useImStoreOut = () => useImStore(pinia);